
import { RefObject } from 'react';
import * as THREE from 'three';
import { createWaveParticles } from '../waveUtils';

interface SceneSetupOptions {
  particleCount: number;
  waveWidth: number;
  waveDepth: number;
  particleSize: number;
}

interface SceneSetupResult {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  particles: THREE.Points;
  geometry: THREE.BufferGeometry;
  positions: Float32Array;
}

/**
 * Initialize the Three.js scene, camera, renderer, and particles
 */
export const initializeScene = (
  containerRef: RefObject<HTMLDivElement>,
  windowSize: { width: number, height: number },
  options: SceneSetupOptions
): SceneSetupResult | null => {
  if (!containerRef.current) return null;

  // Create scene
  const scene = new THREE.Scene();

  // Create camera
  const camera = new THREE.PerspectiveCamera(
    60,
    windowSize.width / windowSize.height,
    0.1,
    1000
  );
  camera.position.z = 10;

  // Create renderer
  const renderer = new THREE.WebGLRenderer({ 
    antialias: true,
    alpha: true,
  });
  renderer.setSize(windowSize.width, windowSize.height);
  renderer.setClearColor(0x000000, 0);
  containerRef.current.appendChild(renderer.domElement);

  // Create particles
  const { geometry, positions } = createWaveParticles(
    options.particleCount,
    options.waveWidth,
    options.waveDepth
  );

  const material = new THREE.PointsMaterial({
    size: options.particleSize,
    vertexColors: true,
    transparent: true,
    opacity: 0.8,
    sizeAttenuation: true,
  });

  const particles = new THREE.Points(geometry, material);
  // Original positioning from the first implementation
  particles.position.y = -3; // Position at bottom of screen
  scene.add(particles);

  return {
    scene,
    camera,
    renderer,
    particles,
    geometry,
    positions
  };
};

/**
 * Clean up Three.js objects and event listeners
 */
export const cleanupScene = (
  containerRef: RefObject<HTMLDivElement>,
  rendererRef: RefObject<THREE.WebGLRenderer>,
  geometryRef: RefObject<THREE.BufferGeometry>,
  particlesRef: RefObject<THREE.Points>,
  frameIdRef: RefObject<number | null>
) => {
  if (frameIdRef.current !== null) {
    cancelAnimationFrame(frameIdRef.current);
  }

  if (rendererRef.current && containerRef.current) {
    containerRef.current.removeChild(rendererRef.current.domElement);
  }

  // Clean up Three.js objects
  if (geometryRef.current) {
    geometryRef.current.dispose();
  }

  if (particlesRef.current && particlesRef.current.material) {
    if (Array.isArray(particlesRef.current.material)) {
      particlesRef.current.material.forEach((material) => material.dispose());
    } else {
      particlesRef.current.material.dispose();
    }
  }

  if (rendererRef.current) {
    rendererRef.current.dispose();
  }
};
